// Alphatech Labz — modern geometric "A" logomark.
// Angular chevron with a signal/scan bar and a "spark" LED,
// filled with the brand's blue→purple→cyan gradient.

const gradientId = "alphatech-logo-gradient";
const glowId = "alphatech-logo-glow";

export function LogoMark({ size = 40, className = "", withGlow = true, animated = true }) {
  const uid = `${gradientId}`;
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      className={className}
      aria-label="Alphatech Labz"
      role="img"
    >
      <defs>
        <linearGradient id={uid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="#2563EB" />
          <stop offset="55%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#00D4FF" />
        </linearGradient>
        <linearGradient id={`${uid}-scan`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%"  stopColor="#00D4FF" stopOpacity="0" />
          <stop offset="50%" stopColor="#00D4FF" stopOpacity="1" />
          <stop offset="100%" stopColor="#00D4FF" stopOpacity="0" />
        </linearGradient>
        {withGlow && (
          <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="0.8" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* Angular "A" — left leg */}
      <path
        d="M 4 34 L 18 6 L 22 6 L 12 34 Z"
        fill={`url(#${uid})`}
        filter={withGlow ? `url(#${glowId})` : undefined}
      />
      {/* Angular "A" — right leg */}
      <path
        d="M 18 6 L 22 6 L 36 34 L 28 34 Z"
        fill={`url(#${uid})`}
        opacity="0.9"
        filter={withGlow ? `url(#${glowId})` : undefined}
      />
      {/* Scan / signal bar (tech accent) */}
      <rect x="10" y="22" width="20" height="2" rx="1" fill={`url(#${uid}-scan)`} />
      {/* Spark LED */}
      <circle cx="22" cy="6" r="1.8" fill="#00D4FF">
        {animated && (
          <animate attributeName="opacity" values="0.4;1;0.4" dur="2.2s" repeatCount="indefinite" />
        )}
      </circle>
    </svg>
  );
}

export default function Logo({
  variant = "full",     // "full" | "mark" | "stack"
  size = "md",           // "sm" | "md" | "lg"
  className = "",
  withGlow = true,
  animated = true,
}) {
  const sizes = {
    sm: { mark: 28, title: "text-sm", sub: "text-[9px]", gap: "gap-2" },
    md: { mark: 36, title: "text-base", sub: "text-[10px]", gap: "gap-2.5" },
    lg: { mark: 52, title: "text-xl", sub: "text-[11px]", gap: "gap-3" },
  };
  const s = sizes[size] || sizes.md;

  if (variant === "mark") {
    return <LogoMark size={s.mark} className={className} withGlow={withGlow} animated={animated} />;
  }

  return (
    <div className={`flex items-center ${s.gap} ${className}`}>
      <LogoMark size={s.mark} withGlow={withGlow} animated={animated} />
      <div className={`leading-tight ${variant === "stack" ? "text-center" : ""}`}>
        <div className={`font-display font-bold text-white tracking-tight ${s.title}`}>
          Alphatech
        </div>
        <div className={`uppercase tracking-[0.32em] text-[#00D4FF]/80 ${s.sub}`}>
          Labz
        </div>
      </div>
    </div>
  );
}
